'use client';

import { Footer } from '@/components/public/footer';
import { NavPreview } from '@/components/public/nav-preview';
import { useEffect, useState, Suspense } from 'react';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';
import { useSearchParams } from 'next/navigation';
import { ArrowRight, ChevronLeft, ChevronRight, ChevronDown, Users, Heart, Camera, Music, Scissors, Cake, Flower2, Tent, Sparkles, X, Upload, Check, Loader2 } from 'lucide-react';
import Image from 'next/image';
import { Link } from '@/i18n/navigation';
import { useTranslations, useLocale } from 'next-intl';
import { FaqAccordion } from '@/components/public/faq-accordion';
import { FAQ_CATEGORIES } from '@/lib/faq-content';
import { useFaqItems } from '@/lib/faq-hooks';
import { log } from '@/lib/logger';

// ─── Image paths ─────────────────────────────────────────────────────────────

const IMG = (n: string) => `/design-images/events/events-${n}.webp`;

// ─── Gallery Component ──────────────────────────────────────────────────────

function EventGallery() {
  const t = useTranslations('design.events');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const galleryImages = Array.from({ length: 38 }, (_, i) => IMG(String(i + 33)));

  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [lightboxIndex]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex((prev) => prev !== null ? (prev + 1) % galleryImages.length : null);
      if (e.key === 'ArrowLeft') setLightboxIndex((prev) => prev !== null ? (prev - 1 + galleryImages.length) % galleryImages.length : null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, galleryImages.length]);

  return (
    <>
      <div className="columns-2 md:columns-3 lg:columns-4 gap-3 space-y-3">
        {galleryImages.map((src, i) => (
          <button
            key={i}
            onClick={() => setLightboxIndex(i)}
            className="block w-full overflow-hidden rounded-xl break-inside-avoid group cursor-pointer"
          >
            <Image
              src={src}
              alt={t('gallery.imageAlt', { number: i + 1 })}
              width={600}
              height={400}
              className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            aria-label={t('gallery.close')}
            className="absolute top-6 right-6 text-white/70 hover:text-white text-4xl z-10"
            onClick={() => setLightboxIndex(null)}
          >
            &times;
          </button>
          <button
            aria-label={t('gallery.previous')}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white z-10"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + galleryImages.length) % galleryImages.length); }}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            aria-label={t('gallery.next')}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white z-10"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % galleryImages.length); }}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <div className="relative max-w-[90vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <Image
              src={galleryImages[lightboxIndex]}
              alt={t('gallery.lightboxAlt', { number: lightboxIndex + 1 })}
              width={1400}
              height={900}
              className="max-h-[85vh] w-auto object-contain"
            />
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-sm">
            {lightboxIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Testimonial Component ──────────────────────────────────────────────────

function TestimonialSection() {
  const t = useTranslations('design.events');
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      text: t('testimonials.0.text'),
      name: t('testimonials.0.name'),
      date: t('testimonials.0.date'),
    },
    {
      text: t('testimonials.1.text'),
      name: t('testimonials.1.name'),
      date: t('testimonials.1.date'),
    },
    {
      text: t('testimonials.2.text'),
      name: t('testimonials.2.name'),
      date: t('testimonials.2.date'),
    },
  ];

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  const current = testimonials[currentIndex];

  return (
    <div className="relative max-w-5xl mx-auto">
      <div className="bg-white rounded-3xl shadow-2xl px-8 md:px-16 lg:px-24 py-10 md:py-16 mx-4 relative overflow-hidden">
        <div className="absolute top-6 left-8 text-[#801515]/10 text-8xl font-serif leading-none">&ldquo;</div>
        <div className="absolute bottom-6 right-8 text-[#801515]/10 text-8xl font-serif leading-none">&rdquo;</div>

        <blockquote className="text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed mb-10 text-center font-light italic relative z-10 min-h-[150px] md:min-h-[120px] flex items-center justify-center transition-opacity duration-500">
          &ldquo;{current.text}&rdquo;
        </blockquote>

        <div className="text-center mb-8">
          <p className="font-semibold text-gray-900 text-xl">{current.name}</p>
          <p className="text-[#801515] font-medium text-lg">{current.date}</p>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-6">
          <button
            onClick={goToPrevious}
            className="w-10 h-10 rounded-full bg-[#801515]/10 hover:bg-[#801515]/20 flex items-center justify-center text-[#801515] transition-colors duration-300"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'bg-[#801515] w-8' : 'bg-[#801515]/30 hover:bg-[#801515]/50'
                }`}
                aria-label={`Testimonial ${index + 1}`}
              />
            ))}
          </div>

          <button
            onClick={goToNext}
            className="w-10 h-10 rounded-full bg-[#801515]/10 hover:bg-[#801515]/20 flex items-center justify-center text-[#801515] transition-colors duration-300"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FAQ Accordion ──────────────────────────────────────────────────────────

function FAQAccordion() {
  const category = FAQ_CATEGORIES.find((c) => c.id === 'events')!;
  const items = useFaqItems(category);
  return <FaqAccordion items={items} accent={category.accent} />;
}

// ─── Event Inquiry Modal ─────────────────────────────────────────────────────

function EventInquiryModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useTranslations('design.events');
  const locale = useLocale();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [period, setPeriod] = useState('');
  const [guests, setGuests] = useState('');
  const [style, setStyle] = useState('');
  const [styleOther, setStyleOther] = useState('');
  const [location, setLocation] = useState('');
  const [menuItems, setMenuItems] = useState<string[]>([]);
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const [recOther, setRecOther] = useState('');
  const [dreamEvent, setDreamEvent] = useState('');
  // Newsletter opt-in → Corporate audience (doc 78 §Go-live)
  const [wantsNewsletter, setWantsNewsletter] = useState(false);

  const eventStyles = [
    t('form.styles.elegant'),
    t('form.styles.formal'),
    t('form.styles.relaxed'),
    t('form.styles.garden'),
    t('form.styles.minimalist'),
    t('form.styles.traditional'),
  ];

  const locationOptions = [t('form.locations.restaurant'), t('form.locations.barn'), t('form.locations.garden')];

  const menuOptions = [
    t('form.menu.welcome'),
    t('form.menu.fingerFood'),
    t('form.menu.starters'),
    t('form.menu.soup'),
    t('form.menu.oneMain'),
    t('form.menu.twoMain'),
    t('form.menu.lateNight'),
  ];

  const recommendationOptions = [
    t('form.recommendations.decor'),
    t('form.recommendations.photo'),
    t('form.recommendations.entertainment'),
    t('form.recommendations.cake'),
    t('form.recommendations.hairMakeup'),
  ];

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => setSubmitted(false), 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch('/api/inquiries/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name, phone, email,
          period, guests,
          style, styleOther,
          location,
          menuItems, recommendations, recOther,
          dreamEvent,
          wantsNewsletter,
          locale,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'send_failed');
      }
      setSubmitted(true);
    } catch (err) {
      log.error('design.events.inquiry.submit_failed', { err });
      setSubmitError(t('form.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleItem = (list: string[], setList: React.Dispatch<React.SetStateAction<string[]>>, item: string) => {
    setList(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl mx-4 my-8 md:my-16 bg-white rounded-3xl shadow-2xl overflow-hidden animate-[slideInUp_0.5s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/20 hover:bg-black/40 backdrop-blur-sm flex items-center justify-center text-white transition-colors duration-300"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Hero image */}
        <div className="relative h-48 md:h-64 overflow-hidden">
          <Image
            src="/design-images/uploads/1774268758201-f-g_0841.jpg"
            alt={t('form.heroAlt')}
            fill
            className="object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        </div>

        {submitted ? (
          /* ── Success state ── */
          <div className="px-8 md:px-16 py-16 text-center">
            <div className="w-20 h-20 rounded-full bg-[#4A6741]/10 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-[#4A6741]" />
            </div>
            <h2 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('form.thankYou')}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-2">
              {t('form.requestReceived')}
            </p>
            <p className="text-gray-500 leading-relaxed mb-10">
              {t('form.replyMessage')}
            </p>
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 bg-[#801515] hover:bg-[#601010] text-white px-8 py-4 rounded-full font-medium transition-all duration-300 hover:shadow-xl"
            >
              {t('form.close')}
            </button>
          </div>
        ) : (
          /* ── Form ── */
          <form onSubmit={handleSubmit} className="px-8 md:px-16 py-10">
            <div className="text-center mb-10">
              <h2 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                {t('form.title')}
              </h2>
              <p className="text-gray-500 leading-relaxed max-w-lg mx-auto">
                {t('form.subtitle')}
              </p>
            </div>

            {/* ── Contact Details ── */}
            <div className="mb-10">
              <h3 className="font-poppins text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#801515]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#801515] text-sm font-bold">1</span>
                </div>
                {t('form.section1')}
              </h3>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('form.fullName')} <span className="text-[#801515]">*</span></label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#faf8f6] focus:border-[#801515] focus:ring-2 focus:ring-[#801515]/10 outline-none transition-all duration-300 text-gray-900"
                    placeholder={t('form.fullNamePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('form.phone')} <span className="text-[#801515]">*</span></label>
                  <input
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#faf8f6] focus:border-[#801515] focus:ring-2 focus:ring-[#801515]/10 outline-none transition-all duration-300 text-gray-900"
                    placeholder="+40 ..."
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('form.email')} <span className="text-[#801515]">*</span></label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#faf8f6] focus:border-[#801515] focus:ring-2 focus:ring-[#801515]/10 outline-none transition-all duration-300 text-gray-900"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-10" />

            {/* ── Event Details ── */}
            <div className="mb-10">
              <h3 className="font-poppins text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#801515]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#801515] text-sm font-bold">2</span>
                </div>
                {t('form.section2')}
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('form.period')}</label>
                  <input
                    type="text"
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#faf8f6] focus:border-[#801515] focus:ring-2 focus:ring-[#801515]/10 outline-none transition-all duration-300 text-gray-900"
                    placeholder={t('form.periodPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('form.guests')}</label>
                  <input
                    type="text"
                    value={guests}
                    onChange={(e) => setGuests(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#faf8f6] focus:border-[#801515] focus:ring-2 focus:ring-[#801515]/10 outline-none transition-all duration-300 text-gray-900"
                    placeholder={t('form.guestsPlaceholder')}
                  />
                </div>
              </div>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-10" />

            {/* ── Event Style ── */}
            <div className="mb-10">
              <h3 className="font-poppins text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#801515]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#801515] text-sm font-bold">3</span>
                </div>
                {t('form.section3')}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {eventStyles.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStyle(s)}
                    className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all duration-300 text-left ${
                      style === s
                        ? 'border-[#801515] bg-[#801515]/5 text-[#801515] shadow-md'
                        : 'border-gray-200 bg-[#faf8f6] text-gray-700 hover:border-[#801515]/30 hover:bg-[#801515]/[0.02]'
                    }`}
                  >
                    {s}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setStyle('Other')}
                  className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all duration-300 text-left ${
                    style === 'Other'
                      ? 'border-[#801515] bg-[#801515]/5 text-[#801515] shadow-md'
                      : 'border-gray-200 bg-[#faf8f6] text-gray-700 hover:border-[#801515]/30 hover:bg-[#801515]/[0.02]'
                  }`}
                >
                  {t('form.other')}
                </button>
              </div>
              {style === 'Other' && (
                <input
                  type="text"
                  value={styleOther}
                  onChange={(e) => setStyleOther(e.target.value)}
                  className="mt-3 w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#faf8f6] focus:border-[#801515] focus:ring-2 focus:ring-[#801515]/10 outline-none transition-all duration-300 text-gray-900"
                  placeholder={t('form.stylePlaceholder')}
                />
              )}
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-10" />

            {/* ── Preferred Location ── */}
            <div className="mb-10">
              <h3 className="font-poppins text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#801515]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#801515] text-sm font-bold">4</span>
                </div>
                {t('form.section4')}
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {locationOptions.map((loc) => (
                  <button
                    key={loc}
                    type="button"
                    onClick={() => setLocation(loc)}
                    className={`px-4 py-4 rounded-xl border text-sm font-medium transition-all duration-300 ${
                      location === loc
                        ? 'border-[#801515] bg-[#801515]/5 text-[#801515] shadow-md'
                        : 'border-gray-200 bg-[#faf8f6] text-gray-700 hover:border-[#801515]/30 hover:bg-[#801515]/[0.02]'
                    }`}
                  >
                    {loc}
                  </button>
                ))}
              </div>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-10" />

            {/* ── Festive Menu ── */}
            <div className="mb-10">
              <h3 className="font-poppins text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#801515]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#801515] text-sm font-bold">5</span>
                </div>
                {t('form.section5')}
              </h3>
              <div className="flex flex-wrap gap-3">
                {menuOptions.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => toggleItem(menuItems, setMenuItems, item)}
                    className={`px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-300 ${
                      menuItems.includes(item)
                        ? 'border-[#801515] bg-[#801515] text-white shadow-md'
                        : 'border-gray-200 bg-[#faf8f6] text-gray-700 hover:border-[#801515]/30'
                    }`}
                  >
                    {menuItems.includes(item) && <Check className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
                    {item}
                  </button>
                ))}
              </div>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-10" />

            {/* ── Recommendations ── */}
            <div className="mb-10">
              <h3 className="font-poppins text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#801515]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#801515] text-sm font-bold">6</span>
                </div>
                {t('form.section6')}
              </h3>
              <div className="flex flex-wrap gap-3">
                {recommendationOptions.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => toggleItem(recommendations, setRecommendations, item)}
                    className={`px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-300 ${
                      recommendations.includes(item)
                        ? 'border-[#801515] bg-[#801515] text-white shadow-md'
                        : 'border-gray-200 bg-[#faf8f6] text-gray-700 hover:border-[#801515]/30'
                    }`}
                  >
                    {recommendations.includes(item) && <Check className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
                    {item}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => toggleItem(recommendations, setRecommendations, 'Other')}
                  className={`px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-300 ${
                    recommendations.includes('Other')
                      ? 'border-[#801515] bg-[#801515] text-white shadow-md'
                      : 'border-gray-200 bg-[#faf8f6] text-gray-700 hover:border-[#801515]/30'
                  }`}
                >
                  {recommendations.includes('Other') && <Check className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
                  {t('form.other')}
                </button>
              </div>
              {recommendations.includes('Other') && (
                <input
                  type="text"
                  value={recOther}
                  onChange={(e) => setRecOther(e.target.value)}
                  className="mt-3 w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#faf8f6] focus:border-[#801515] focus:ring-2 focus:ring-[#801515]/10 outline-none transition-all duration-300 text-gray-900"
                  placeholder={t('form.otherPlaceholder')}
                />
              )}
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-10" />

            {/* ── Dream Event ── */}
            <div className="mb-10">
              <h3 className="font-poppins text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#801515]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#801515] text-sm font-bold">7</span>
                </div>
                {t('form.section7')}
              </h3>
              <textarea
                value={dreamEvent}
                onChange={(e) => setDreamEvent(e.target.value)}
                rows={5}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#faf8f6] focus:border-[#801515] focus:ring-2 focus:ring-[#801515]/10 outline-none transition-all duration-300 text-gray-900 resize-none"
                placeholder={t('form.dreamPlaceholder')}
              />

              {/* File upload placeholder */}
              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-2">{t('form.uploadLabel')}</p>
                <label className="flex items-center justify-center gap-3 px-6 py-8 border-2 border-dashed border-gray-200 rounded-xl bg-[#faf8f6] hover:border-[#801515]/30 transition-colors duration-300 cursor-pointer group">
                  <Upload className="w-5 h-5 text-gray-400 group-hover:text-[#801515]/50 transition-colors" />
                  <span className="text-sm text-gray-400 group-hover:text-gray-600 transition-colors">{t('form.uploadCta')}</span>
                  <input type="file" className="hidden" accept="image/*" multiple />
                </label>
              </div>
            </div>

            {/* ── Newsletter opt-in ── */}
            <label className="flex items-start gap-3 mx-auto max-w-md mb-6 cursor-pointer text-left">
              <input
                type="checkbox"
                checked={wantsNewsletter}
                onChange={(e) => setWantsNewsletter(e.target.checked)}
                className="mt-1 h-5 w-5 rounded border-gray-300 text-[#801515] focus:ring-[#801515]/30"
              />
              <span className="text-sm text-gray-600">{t('form.newsletterOptIn')}</span>
            </label>

            {/* ── Submit ── */}
            <div className="text-center pt-2 pb-4">
              {submitError && (
                <div className="mb-4 mx-auto max-w-md px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                  {submitError}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting || !name || !phone || !email}
                className="inline-flex items-center gap-3 bg-[#801515] hover:bg-[#601010] disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-10 py-4 rounded-full font-medium text-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 group"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('form.sending')}
                  </>
                ) : (
                  <>
                    {t('form.submitButton')}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function EventsPageWrapper() {
  return (
    <Suspense>
      <EventsPage />
    </Suspense>
  );
}

function EventsPage() {
  const t = useTranslations('design.events');
  const locale = useLocale();
  const [galleryExpanded, setGalleryExpanded] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const searchParams = useSearchParams();

  const civilVenues = [
    { name: t('civil.ballroom.name'), image: IMG('1'), description: t('civil.ballroom.description') },
    { name: t('civil.frontGarden.name'), image: IMG('2'), description: t('civil.frontGarden.description') },
    { name: t('civil.mainGarden.name'), image: IMG('3'), description: t('civil.mainGarden.description') },
  ];

  const celebrationVenues = [
    {
      name: t('celebration.grillTerrace.name'),
      subtitle: t('celebration.grillTerrace.subtitle'),
      image: IMG('4'),
      capacity: t('celebration.grillTerrace.capacity'),
      amenities: [t('celebration.amenities.seating'), t('celebration.amenities.logistics'), t('celebration.amenities.staff')],
    },
    {
      name: t('celebration.restaurant.name'),
      subtitle: t('celebration.restaurant.subtitle'),
      image: IMG('5'),
      capacity: t('celebration.restaurant.capacity'),
      amenities: [t('celebration.amenities.seatingLinens'), t('celebration.amenities.bar'), t('celebration.amenities.logistics'), t('celebration.amenities.staff'), t('celebration.amenities.sound')],
    },
    {
      name: t('celebration.barn.name'),
      subtitle: t('celebration.barn.subtitle'),
      image: IMG('6'),
      capacity: t('celebration.barn.capacity'),
      amenities: [t('celebration.amenities.roundTables'), t('celebration.amenities.linens'), t('celebration.amenities.barArea'), t('celebration.amenities.danceFloor'), t('celebration.amenities.lounge'), t('celebration.amenities.soundSystem'), t('celebration.amenities.logistics')],
    },
    {
      name: t('celebration.garden.name'),
      subtitle: t('celebration.garden.subtitle'),
      image: IMG('7'),
      capacity: t('celebration.garden.capacity'),
      amenities: [t('celebration.amenities.roundTables'), t('celebration.amenities.linens'), t('celebration.amenities.barArea'), t('celebration.amenities.danceFloor'), t('celebration.amenities.loungeFlame'), t('celebration.amenities.logistics')],
    },
  ];

  const partnerCategories = [
    { name: t('partners.decor'), image: IMG('24'), icon: Flower2 },
    { name: t('partners.photo'), image: IMG('25'), icon: Camera },
    { name: t('partners.tent'), image: IMG('26'), icon: Tent },
    { name: t('partners.hair'), image: IMG('27'), icon: Scissors },
    { name: t('partners.cake'), image: IMG('28'), icon: Cake },
    { name: t('partners.music'), image: IMG('29'), icon: Music },
  ];

  const guestActivities = [
    { name: t('guestActivities.spa.name'), image: IMG('30'), description: t('guestActivities.spa.description') },
    { name: t('guestActivities.exhibition.name'), image: IMG('31'), description: t('guestActivities.exhibition.description') },
    { name: t('guestActivities.hike.name'), image: IMG('32'), description: t('guestActivities.hike.description') },
  ];

  const additionalStations = [
    { name: t('dining.stations.cheese'), image: IMG('18') },
    { name: t('dining.stations.fruit'), image: IMG('19') },
    { name: t('dining.stations.savory'), image: IMG('21') },
  ];

  // Open inquiry form if ?inquiry=open is in the URL
  useEffect(() => {
    if (searchParams.get('inquiry') === 'open') {
      setInquiryOpen(true);
    }
  }, [searchParams]);

  // Scroll-triggered animations
  useScrollReveal();

  // Trigger hero animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <main className="min-h-screen bg-white text-gray-900">
      <style jsx global>{`
        @keyframes breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes breatheSlow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .image-breathe {
          animation: breathe 8s ease-in-out infinite;
        }
        .image-breathe-slow {
          animation: breatheSlow 10s ease-in-out infinite;
        }
      `}</style>

      <NavPreview />

      {/* ━━━ HERO ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <Image src={IMG('hero')} alt={t('hero.imageAlt')} fill className="object-cover image-breathe-slow" priority />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/70" />

        <div className={`relative h-full flex flex-col items-center justify-center px-6 text-center transition-all duration-[2000ms] ease-out ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Heart className="w-10 h-10 text-white/60 mb-6" />
          <h1 className="font-poppins text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight" style={{ textShadow: '2px 2px 30px rgba(0,0,0,0.5)' }}>
            {t('hero.title')}
          </h1>
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-[2px] w-20 bg-[#801515]" style={{ animation: 'pulse 3s ease-in-out infinite' }} />
            <p className="text-base md:text-lg uppercase tracking-[0.3em] text-white/90 [text-shadow:rgba(0,0,0,1)_1px_1px_3px] md:[text-shadow:rgb(0,0,0)_1.5px_1.5px_4px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>
              {t('hero.subtitle')}
            </p>
            <div className="h-[2px] w-20 bg-[#801515]" style={{ animation: 'pulse 3s ease-in-out infinite', animationDelay: '1.5s' }} />
          </div>
          <p className="text-3xl md:text-4xl text-white/95 w-fit max-w-3xl mx-auto font-light [text-shadow:rgba(0,0,0,1)_1.5px_1.5px_4px] md:[text-shadow:rgb(0,0,0)_2px_2px_6px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>
            {t('hero.tagline')}
          </p>

          {/* Scroll indicator */}
          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50">
            <span className="text-xs uppercase tracking-widest">{t('hero.discover')}</span>
            <ChevronDown className="w-5 h-5 animate-bounce" />
          </div>
        </div>

      </section>

      {/* ━━━ WEDDINGS INTRO ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-[#faf8f6]">
          <div className="max-w-lg observe-me slide-left">
            <p className="text-[#801515] uppercase tracking-[0.25em] text-sm font-medium mb-4">{t('intro.label')}</p>
            <h2 className="font-poppins text-4xl md:text-5xl xl:text-6xl font-bold mb-4 leading-[1.1]">
              {t('intro.heading1')}
              <br />
              <span className="text-[#801515] italic font-light">{t('intro.heading2')}</span>
            </h2>
            <p className="text-2xl md:text-3xl text-gray-700 font-light mb-6">
              {t('intro.heading3')}
            </p>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg text-gray-600 leading-relaxed mb-4">
              {t('intro.p1')}
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              {t('intro.p2')}
            </p>
            <div className="bg-white rounded-2xl p-6 shadow-md border border-[#801515]/10">
              <p className="text-xl font-poppins text-gray-800 text-center italic">
                {t('intro.tagline1')}
                <br />
                <span className="text-[#801515] font-semibold not-italic">{t('intro.tagline2')}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 observe-me slide-right">
          <Image src={IMG('6')} alt={t('intro.imageAlt')} fill className="object-cover image-breathe hover:scale-110 transition-transform duration-700" />
        </div>
      </section>

      {/* ━━━ CIVIL CEREMONY VENUES ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap">
        <div className="max-w-7xl mx-auto design-content-pad">
          <div className="text-center mb-16 observe-me slide-up">
            <p className="text-[#801515] uppercase tracking-[0.25em] text-sm font-medium mb-4">{t('civil.label')}</p>
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold text-gray-900 mb-4">
              {t('civil.heading')}
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              {t('civil.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {civilVenues.map((venue, i) => (
              <div
                key={venue.name}
                className="observe-me slide-up group relative rounded-3xl overflow-hidden h-[420px] shadow-lg hover:shadow-2xl transition-all duration-500"
                style={{ animationDelay: `${i * 0.15}s` }}
              >
                <Image src={venue.image} alt={venue.name} fill className="object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl font-bold text-white font-poppins mb-2">{venue.name}</h3>
                  <p className="text-white/80 text-sm leading-relaxed">{venue.description}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-500 mt-8 observe-me fade-in">
            <em>{t('civil.religiousNote')}</em>
          </p>
        </div>
      </section>

      {/* ━━━ GRAND CELEBRATION VENUES ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap bg-[#faf8f6] rounded-3xl overflow-hidden">
        <div className="max-w-7xl mx-auto design-content-pad lg:py-20">
          <div className="text-center mb-16 observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold text-gray-900 mb-4">
              {t('celebration.heading')}
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              {t('celebration.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {celebrationVenues.map((venue, i) => (
              <div
                key={venue.name}
                className="observe-me slide-up bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500"
                style={{ animationDelay: `${i * 0.12}s` }}
              >
                <div className="relative h-64 overflow-hidden">
                  <Image src={venue.image} alt={venue.name} fill className="object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-6 right-6">
                    <h3 className="text-2xl font-bold text-white font-poppins">{venue.name}</h3>
                    <p className="text-white/80 text-sm italic">{venue.subtitle}</p>
                  </div>
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full">
                    <span className="text-[#801515] font-bold text-sm flex items-center gap-1">
                      <Users className="w-4 h-4" /> {venue.capacity}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <p className="text-sm font-semibold text-gray-800 mb-3">{t('celebration.venueProvides')}</p>
                  <div className="flex flex-wrap gap-2">
                    {venue.amenities.map((a) => (
                      <span key={a} className="bg-[#faf8f6] text-gray-700 px-3 py-1.5 rounded-full text-sm">
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-500 mt-10 observe-me fade-in text-sm">
            {t('celebration.rentalNote')}
          </p>
        </div>
      </section>

      {/* ━━━ GETTING READY ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="grid lg:grid-cols-5 min-h-[80vh] design-section-gap rounded-3xl overflow-hidden">
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 lg:order-1">
          <div className="grid grid-cols-2 h-full">
            <div className="relative overflow-hidden observe-me slide-left">
              <Image src={IMG('8')} alt={t('gettingReady.bridalAlt')} fill className="object-cover image-breathe hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <p className="absolute bottom-4 left-4 text-white font-poppins text-lg font-semibold">{t('gettingReady.bridalParty')}</p>
            </div>
            <div className="relative overflow-hidden observe-me slide-right">
              <Image src={IMG('9')} alt={t('gettingReady.groomAlt')} fill className="object-cover image-breathe hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <p className="absolute bottom-4 left-4 text-white font-poppins text-lg font-semibold">{t('gettingReady.groomSquad')}</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-white order-1 lg:order-2">
          <div className="max-w-lg observe-me slide-right">
            <p className="text-[#801515] uppercase tracking-[0.25em] text-sm font-medium mb-4">{t('gettingReady.label')}</p>
            <h2 className="font-poppins text-4xl md:text-5xl xl:text-6xl font-bold mb-6 leading-[1.1]">
              <span className="text-gray-900">{t('gettingReady.heading1')}</span>
              <br />
              <span className="text-[#801515] italic font-light">{t('gettingReady.heading2')}</span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg text-gray-600 leading-relaxed">
              {t('gettingReady.description')}
            </p>
          </div>
        </div>
      </section>

      {/* ━━━ DINING & DRINKS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap bg-[#1a1010] text-white rounded-3xl overflow-hidden">
        <div className="max-w-7xl mx-auto design-content-pad lg:py-20">
          <div className="text-center mb-16 observe-me slide-up">
            <Sparkles className="w-8 h-8 text-[#d4b896] mx-auto mb-4" />
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-4">
              {t('dining.heading')}
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              {t('dining.description')}
            </p>
          </div>

          {/* Friday Grill Party */}
          <div className="mb-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center observe-me slide-up">
              <div>
                <p className="text-[#d4b896] uppercase tracking-[0.25em] text-sm font-medium mb-3">{t('dining.fridayLabel')}</p>
                <h3 className="font-poppins text-3xl md:text-4xl font-bold mb-6">{t('dining.welcomeDinner')}</h3>
                <p className="text-white/70 text-lg leading-relaxed mb-6">
                  {t('dining.welcomeP1')}
                </p>
                <p className="text-white/70 text-lg leading-relaxed mb-6">
                  {t('dining.welcomeP2')}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 h-[300px]">
                <div className="relative rounded-2xl overflow-hidden">
                  <Image src={IMG('10')} alt={t('dining.welcomeAlt1')} fill className="object-cover" />
                </div>
                <div className="relative rounded-2xl overflow-hidden">
                  <Image src={IMG('11')} alt={t('dining.welcomeAlt2')} fill className="object-cover" />
                </div>
                <div className="relative rounded-2xl overflow-hidden">
                  <Image src={IMG('12')} alt={t('dining.welcomeAlt3')} fill className="object-cover" />
                </div>
              </div>
            </div>
          </div>

          {/* Saturday Festive Menu */}
          <div className="mb-20">
            <div className="text-center mb-12 observe-me slide-up">
              <p className="text-[#d4b896] uppercase tracking-[0.25em] text-sm font-medium mb-3">{t('dining.saturdayLabel')}</p>
              <h3 className="font-poppins text-3xl md:text-4xl font-bold">{t('dining.festiveMenu')}</h3>
            </div>

            {/* Starters */}
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16 observe-me slide-up">
              <div className="order-2 lg:order-1 grid grid-cols-3 gap-3 h-[250px]">
                <div className="relative rounded-2xl overflow-hidden"><Image src={IMG('13')} alt={t('dining.starterAlt1')} fill className="object-cover" /></div>
                <div className="relative rounded-2xl overflow-hidden"><Image src={IMG('14')} alt={t('dining.starterAlt2')} fill className="object-cover" /></div>
                <div className="relative rounded-2xl overflow-hidden"><Image src={IMG('15')} alt={t('dining.starterAlt3')} fill className="object-cover" /></div>
              </div>
              <div className="order-1 lg:order-2">
                <h4 className="text-2xl font-poppins font-semibold mb-4 text-[#d4b896]">{t('dining.starters')}</h4>
                <p className="text-white/70 leading-relaxed mb-4">{t('dining.startersDesc')}</p>
                <div className="grid grid-cols-2 gap-3 text-sm text-white/60">
                  <div className="bg-white/5 rounded-xl p-3">
                    <p className="text-white/90 font-medium mb-1">{t('dining.meatBased')}</p>
                    <p>{t('dining.meatItems')}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3">
                    <p className="text-white/90 font-medium mb-1">{t('dining.vegetarian')}</p>
                    <p>{t('dining.vegItems')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Soup + Main */}
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16 observe-me slide-up">
              <div>
                <h4 className="text-2xl font-poppins font-semibold mb-4 text-[#d4b896]">{t('dining.soupMain')}</h4>
                <p className="text-white/70 leading-relaxed mb-4">{t('dining.soupMainDesc')}</p>
              </div>
              <div className="grid grid-cols-2 gap-3 h-[220px]">
                <div className="relative rounded-2xl overflow-hidden"><Image src={IMG('16')} alt={t('dining.soupAlt')} fill className="object-cover" /></div>
                <div className="relative rounded-2xl overflow-hidden"><Image src={IMG('17')} alt={t('dining.mainAlt')} fill className="object-cover" /></div>
              </div>
            </div>

            {/* Additional Stations */}
            <div className="mb-16 observe-me slide-up">
              <h4 className="text-2xl font-poppins font-semibold mb-6 text-center text-[#d4b896]">{t('dining.additionalStations')}</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {additionalStations.map((station) => (
                  <div key={station.name} className="relative rounded-2xl overflow-hidden h-[200px] group">
                    <Image src={station.image} alt={station.name} fill className="object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <p className="absolute bottom-4 left-4 text-white font-poppins font-semibold">{station.name}</p>
                  </div>
                ))}
              </div>
              <div className="text-center mt-6">
                <Link href="/dining" className="inline-flex items-center gap-2 text-[#d4b896] hover:text-white transition-colors duration-300 font-medium">
                  {t('dining.consultMenu')} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Open Bar */}
            <div className="grid lg:grid-cols-2 gap-12 items-center observe-me slide-up">
              <div>
                <h4 className="text-2xl font-poppins font-semibold mb-4 text-[#d4b896]">{t('dining.openBar')}</h4>
                <p className="text-white/70 leading-relaxed mb-6">{t('dining.openBarDesc')}</p>
                <ul className="space-y-3 text-white/70">
                  {[
                    t('dining.barItem1'),
                    t('dining.barItem2'),
                    t('dining.barItem3'),
                    t('dining.barItem4'),
                    t('dining.barItem5'),
                    t('dining.barItem6'),
                    t('dining.barItem7'),
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-1.5 h-1.5 bg-[#d4b896] rounded-full mt-2.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative rounded-2xl overflow-hidden h-[350px]">
                <Image src={IMG('22')} alt={t('dining.openBarAlt')} fill className="object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ ACCOMMODATION ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="grid lg:grid-cols-5 min-h-[90vh] design-section-gap rounded-3xl overflow-hidden">
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-[#faf8f6]">
          <div className="max-w-lg observe-me slide-left">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('stay.headingBold')}</span>
              <br />
              <span className="text-gray-800 text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('stay.headingLight')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8">
              {t('stay.description')}
            </p>
            <Link
              href="/rooms"
              className="inline-flex items-center gap-3 bg-[#801515] hover:bg-[#601010] text-white px-8 py-4 rounded-full font-medium transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 group"
            >
              {t('stay.seeRooms')}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 observe-me slide-right">
          <Image src={IMG('23')} alt={t('stay.imageAlt')} fill className="object-cover image-breathe hover:scale-110 transition-transform duration-700" />
        </div>
      </section>

      {/* ━━━ TRUSTED PARTNERS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap">
        {locale === 'ro' ? (
          <div className="max-w-7xl mx-auto design-content-pad">
            <div className="text-center mb-12 observe-me slide-up">
              <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold text-gray-900 mb-4">
                {t('partners.label')}
              </h2>
              <p className="text-xl text-gray-600 font-light mb-8">
                {t('partners.heading')}
              </p>
              <div className="w-16 h-1 bg-[#801515] mx-auto mb-8" />
            </div>

            <div className="max-w-3xl mx-auto text-center observe-me fade-in mb-16">
              <div className="text-lg text-gray-600 leading-relaxed">
                {t('partners.description').split('\n').map((line, i) => (
                  <p key={i} className="mb-4">
                    {line}
                  </p>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {partnerCategories.map((partner, i) => {
                const Icon = partner.icon;
                return (
                  <div
                    key={partner.name}
                    className="observe-me slide-up relative rounded-2xl overflow-hidden h-[260px] group cursor-default"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  >
                    <Image src={partner.image} alt={partner.name} fill className="object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent group-hover:from-black/80 transition-all duration-500" />
                    <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-white font-poppins font-semibold text-sm md:text-base">{partner.name}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto design-content-pad">
            <div className="text-center mb-6 observe-me slide-up">
              <p className="text-[#801515] uppercase tracking-[0.25em] text-sm font-medium mb-4">{t('partners.label')}</p>
              <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold text-gray-900 mb-4">
                {t('partners.heading')}
              </h2>
            </div>
            <div className="max-w-3xl mx-auto text-center mb-16 observe-me fade-in">
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('partners.description')}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {partnerCategories.map((partner, i) => {
                const Icon = partner.icon;
                return (
                  <div
                    key={partner.name}
                    className="observe-me slide-up relative rounded-2xl overflow-hidden h-[260px] group cursor-default"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  >
                    <Image src={partner.image} alt={partner.name} fill className="object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent group-hover:from-black/80 transition-all duration-500" />
                    <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-white font-poppins font-semibold text-sm md:text-base">{partner.name}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* ━━━ ACTIVITIES FOR GUESTS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap bg-[#faf8f6] rounded-3xl overflow-hidden">
        <div className="max-w-7xl mx-auto design-content-pad lg:py-20">
          <div className="text-center mb-16 observe-me slide-up">
            <p className="text-[#4A6741] uppercase tracking-[0.25em] text-sm font-medium mb-4">{t('guestActivities.label')}</p>
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold text-gray-900 mb-4">
              {t('guestActivities.heading')}
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              {t('guestActivities.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {guestActivities.map((activity, i) => (
              <div key={activity.name} className="observe-me slide-up group" style={{ animationDelay: `${i * 0.15}s` }}>
                <div className="relative rounded-3xl overflow-hidden h-[300px] mb-6 shadow-lg group-hover:shadow-2xl transition-shadow duration-500">
                  <Image src={activity.image} alt={activity.name} fill className="object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                </div>
                <h3 className="font-poppins text-xl font-bold text-gray-900 mb-2">{activity.name}</h3>
                <p className="text-gray-600 leading-relaxed">{activity.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 observe-me fade-in">
            <Link
              href="/activities"
              className="inline-flex items-center gap-3 bg-[#4A6741] hover:bg-[#3a5633] text-white px-8 py-4 rounded-full font-medium transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 group"
            >
              {t('guestActivities.seeAll')}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* ━━━ GALLERY ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap">
        <div className="max-w-7xl mx-auto design-content-pad">
          <div className="text-center mb-16 observe-me slide-up">
            <p className="text-[#801515] uppercase tracking-[0.25em] text-sm font-medium mb-4">{t('gallery.label')}</p>
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold text-gray-900 mb-4">
              {t('gallery.heading')}
            </h2>
          </div>

          <div className={`relative overflow-hidden transition-all duration-700 ${galleryExpanded ? 'max-h-none' : 'max-h-[800px]'}`}>
            <EventGallery />
            {!galleryExpanded && (
              <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-white to-transparent pointer-events-none" />
            )}
          </div>

          {!galleryExpanded && (
            <div className="text-center mt-8">
              <button
                onClick={() => setGalleryExpanded(true)}
                className="inline-flex items-center gap-2 bg-[#801515] hover:bg-[#601010] text-white px-8 py-4 rounded-full font-medium transition-all duration-300 hover:shadow-xl"
              >
                {t('gallery.viewFull')}
                <ChevronDown className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Event highlights videos */}
          <div className="mt-12 grid md:grid-cols-2 gap-8 observe-me slide-up">
            {/* First video */}
            <div className="relative rounded-3xl overflow-hidden">
              <video
                src="https://cxtacwxwreiineqjrbci.supabase.co/storage/v1/object/public/design-videos/uploads/1774266895849-richard-and-maryna-reel_horizontal.mp4"
                controls
                autoPlay
                muted
                loop
                playsInline
                className="w-full rounded-3xl"
              />
              <p className="text-gray-600 text-sm mt-3 text-center">{t('videos.videographer1')}</p>
            </div>

            {/* Second video */}
            <div className="relative rounded-3xl overflow-hidden">
              <video
                src="https://cxtacwxwreiineqjrbci.supabase.co/storage/v1/object/public/design-videos/uploads/1782891617594-r-d-preview.mp4"
                controls
                autoPlay
                muted
                loop
                playsInline
                className="w-full rounded-3xl"
              />
              <p className="text-gray-600 text-sm mt-3 text-center">{t('videos.videographer2')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ TESTIMONIALS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap bg-[#faf8f6] rounded-3xl overflow-hidden">
        <div className="max-w-7xl mx-auto design-content-pad lg:py-20">
          <div className="text-center mb-16 observe-me slide-up">
            <p className="text-[#801515] uppercase tracking-[0.25em] text-sm font-medium mb-4">{t('testimonialSection.label')}</p>
            <h2 className="font-poppins text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('testimonialSection.heading')}
            </h2>
          </div>

          <div className="observe-me slide-up">
            <TestimonialSection />
          </div>
        </div>
      </section>

      {/* ━━━ INQUIRE CTA ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap">
        <div className="relative rounded-3xl overflow-hidden min-h-[60vh]">
          <Image src={IMG('7')} alt={t('cta.imageAlt')} fill className="object-cover image-breathe-slow" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />

          <div className="relative h-full flex items-center min-h-[60vh] design-content-pad lg:p-20">
            <div className="max-w-xl observe-me slide-left">
              <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold text-white mb-6 leading-[1.1]">
                {t('cta.heading1')}
                <br />
                <span className="italic font-light">{t('cta.heading2')}</span>
              </h2>
              <p className="text-white/80 text-lg mb-10 leading-relaxed">
                {t('cta.description')}
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setInquiryOpen(true)}
                  className="inline-flex items-center gap-3 bg-[#801515] hover:bg-[#601010] text-white px-8 py-4 rounded-full font-medium transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 group"
                >
                  {t('cta.button')}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ MAJOR LIFE EVENTS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap">
        {locale === 'hu' ? (
          <>
            <div className="bg-[#faf8f6] rounded-3xl overflow-hidden">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="design-content-pad lg:p-12 xl:p-16 flex items-center">
                  <div className="observe-me slide-left">
                    <p className="text-[#801515] uppercase tracking-[0.25em] text-sm font-medium mb-4">
                      {t('lifeEvents.label')}
                    </p>
                    <h3 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                      {t('lifeEvents.heading')}
                    </h3>
                    <p className="text-lg text-gray-600 leading-relaxed mb-6">
                      {t('lifeEvents.description')}
                    </p>
                    <p className="text-lg text-gray-600 leading-relaxed mb-6">
                      {t('lifeEvents.p1')}
                    </p>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {t('lifeEvents.p2')}
                    </p>
                  </div>
                </div>
                <div className="relative h-[350px] lg:h-auto overflow-hidden observe-me slide-right">
                  <Image src={IMG('71')} alt={t('lifeEvents.imageAlt')} fill className="object-cover" />
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="max-w-7xl mx-auto design-content-pad">
              <div className="text-center mb-16 observe-me slide-up">
                <p className="text-[#801515] uppercase tracking-[0.25em] text-sm font-medium mb-4">{t('lifeEvents.label')}</p>
                <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold text-gray-900 mb-4">
                  {t('lifeEvents.heading')}
                </h2>
                <p className="text-lg text-gray-500 max-w-md mx-auto">
                  {t('lifeEvents.description')}
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
                <div className="observe-me slide-left">
                  <p className="text-lg text-gray-600 leading-relaxed mb-6">
                    {t('lifeEvents.p1')}
                  </p>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {t('lifeEvents.p2')}
                  </p>
                </div>
                <div className="relative rounded-3xl overflow-hidden h-[400px] observe-me slide-right">
                  <Image src={IMG('71')} alt={t('lifeEvents.imageAlt')} fill className="object-cover" />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Creative Photo Shoot */}
        <div className="bg-[#faf8f6] rounded-3xl overflow-hidden observe-me slide-up">
            <div className="grid lg:grid-cols-2 gap-0">
              <div className="relative h-[350px] lg:h-auto overflow-hidden">
                <Image src={IMG('73')} alt={t('photoShoot.imageAlt')} fill className="object-cover" />
              </div>
              <div className="design-content-pad lg:p-12 xl:p-16 flex items-center">
                <div>
                  <p className="text-[#801515] uppercase tracking-[0.25em] text-sm font-medium mb-4">
                    <Camera className="w-4 h-4 inline mr-2" />
                    {t('photoShoot.label')}
                  </p>
                  <h3 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {t('photoShoot.heading')}
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-6">
                    {t('photoShoot.description')}
                  </p>
                  <a
                    href="tel:+40725942787"
                    className="inline-flex items-center gap-3 text-[#801515] font-medium hover:text-[#601010] transition-colors duration-300 group"
                  >
                    {t('photoShoot.cta')}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Photo Shoot Gallery */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6 observe-me slide-up">
            {[IMG('71'), IMG('72'), IMG('73'), IMG('74'), IMG('75')].map((src, i) => (
              <div key={i} className="relative rounded-xl overflow-hidden h-[180px]">
                <Image src={src} alt={t('photoShoot.galleryAlt', { number: i + 1 })} fill className="object-cover hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
      </section>

      {/* ━━━ FAQ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap">
        <div className="max-w-4xl mx-auto design-content-pad lg:py-20">
          <div className="text-center mb-16 observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6">
              <span className="text-[#801515]">{t('faqSection.label')}</span>
              <br />
              <span className="text-gray-800 text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('faqSection.heading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mx-auto" />
          </div>

          <FAQAccordion />
        </div>
      </section>

      <Footer />

      {/* Floating Enquiry Button */}
      <button
        onClick={() => setInquiryOpen(true)}
        className="fixed right-8 z-50 inline-flex items-center gap-3 bg-[#801515] hover:bg-[#601010] text-white px-8 py-4 rounded-full font-medium shadow-2xl transition-all duration-300 hover:shadow-[0_8px_30px_rgba(128,21,21,0.4)] hover:-translate-y-0.5 group"
        style={{ bottom: 'calc(2rem + var(--selection-bar-height))' }}
      >
        {t('cta.button')}
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </button>

      {/* Event Inquiry Modal */}
      <EventInquiryModal open={inquiryOpen} onClose={() => setInquiryOpen(false)} />
    </main>
  );
}
